import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface FeedbackData {
    id: number;
    feedbackContent: string;
    userId: number;
    subject: string | null;
    createdDate: string;
    userName: string;
    email: string;
    phoneNumber: number;
}

const FeedbackDetails: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const feedback = location.state?.feedbackData as FeedbackData;

    if (!feedback) {
        return (
            <div className="min-h-screen bg-magnolia p-6">
                <div className="bg-white rounded-lg p-6">
                    <p className="text-red-600">No feedback data found.</p>
                    <button 
                        onClick={() => navigate(-1)}
                        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ma p-8">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl mx-auto backdrop-blur-sm bg-opacity-95">
            <h1 className="text-3xl font-bold mb-8 text-gray-800 border-b pb-4 flex items-center">
                <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
                Feedback Details
            </h1>
            
            <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md transform hover:scale-[1.02] transition-transform">
                        <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"></path>
                            </svg>
                            User Name
                        </h2>
                        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                            <p className="text-blue-600 font-medium">
                                {feedback.userName}
                            </p>
                        </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md transform hover:scale-[1.02] transition-transform">
                        <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                            </svg>
                            Email
                        </h2>
                        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                            <p className="text-blue-600 font-medium">
                                {feedback.email}
                            </p>
                        </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md transform hover:scale-[1.02] transition-transform">
                        <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                            </svg>
                            Phone Number
                        </h2>
                        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                            <p className="text-blue-600 font-medium">
                                {feedback.phoneNumber}
                            </p>
                        </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md transform hover:scale-[1.02] transition-transform">
                        <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"></path>
                            </svg>
                            Created Date
                        </h2>
                        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                            <p className="text-blue-600 font-medium">
                                {feedback.createdDate}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md">
                    <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z"></path>
                        </svg>
                        Subject
                    </h2>
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                        <p className="text-blue-600 font-medium whitespace-pre-line break-words">
                            {feedback.subject || 'No subject'}
                        </p>
                    </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md">
                    <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H6z"></path>
                        </svg>
                        Feedback Content
                    </h2>
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 min-h-[120px]">
                        <p className="text-blue-600 font-medium whitespace-pre-line break-words">
                            {feedback.feedbackContent || 'No content'}
                        </p>
                    </div>
                </div>
            </div>

            <button 
                onClick={() => navigate(-1)}
                className="mt-8 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 
                         transition-all duration-200 flex items-center gap-2 hover:gap-3"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Go Back
            </button>
        </div>
    </div>
    );
};

export default FeedbackDetails;
